import * as React from 'react';
import { Popover, ListGroup, ListGroupItem, PopoverBody, Button } from 'reactstrap';
import { Routes, makeSimpleRoutePath, FaqSection, makeFaqPath, makeUserPath } from '../utils/routerHelper';
import ProfilePhoto from './ProfilePhoto';
import { UserRead, Entity, Competition } from '../userApi';
import { TranslationFunction } from 'i18next';
import { History } from 'history';
import LinkButton from './LinkButton';
import RouteLink from '../shared/RouteLink';
import { Link } from 'react-router-dom';
import { scrollToLoginForm } from '../routes/Home';
import { campusesLabelFor, teamsLabelFor } from '../utils/competitionLabels';
import MobilePrimaryNav from './MobilePrimaryNav';

interface Props {
  t: TranslationFunction;
  history: History;
  logout: () => void;
  user: UserRead;
  university: Entity;
  competition: Competition;
  loggedIn: boolean;
  isProduction: boolean;
  canManageEmailTemplates: boolean;
  canViewCommsTools: boolean;
  /** When true, render the sticky mobile primary tab bar. */
  showMobilePrimaryNav?: boolean;
}

interface NavBarDropdownState {
  isOpen: boolean;
  isMobileOpen: boolean;
}

const HOVER_CLOSE_DELAY_MS = 200;

export default class NavBarDropdown extends React.Component<Props, NavBarDropdownState> {
  public state = { isOpen: false, isMobileOpen: false };
  private closeTimer: number | null = null;

  public componentWillUnmount() {
    this.clearCloseTimer();
  }

  public openMobileMenu = () => {
    this.setState({ isMobileOpen: true });
  }

  public render() {
    const {
      t, history, logout, user, university, competition, loggedIn, isProduction,
      canManageEmailTemplates, canViewCommsTools, showMobilePrimaryNav
    } = this.props;
    const campusesLabel = campusesLabelFor(competition, t('Campuses'));
    const teamsLabel = teamsLabelFor(competition, t('Teams'));
    return (
      <div>
        {showMobilePrimaryNav && (
          <MobilePrimaryNav onOpenMenu={this.openMobileMenu} />
        )}
        <div
          id="cc__nav_bar__mobile__overlay"
          className={this.state.isMobileOpen ? 'show' : '' }
          aria-hidden={!this.state.isMobileOpen}
        >
          <div  id="cc__nav_bar__mobile__close">
            <button
              type="button"
              className="cc__nav_bar__mobile__close_btn"
              onClick={() => this.setState({isMobileOpen: false})}
              aria-label={t('Close menu')}
            >
              <img alt="" aria-hidden="true" src="/assets/navbar/mobile/times.png"
                srcSet="/assets/navbar/mobile/times@2x.png 2x,
                        /assets/navbar/mobile/times@3x.png 3x" />
            </button>
          </div>
          <div  id="cc__nav_bar__mobile__container">
            {loggedIn && (
              <div  id="cc__nav_bar__mobile__links">
                  <RouteLink activable={true} route={Routes.Dashboard} onClick={() => this.setState({isMobileOpen: false})}>{t('HOME')}</RouteLink>
                  <RouteLink activable={true} route={Routes.Pledges}  onClick={() => this.setState({isMobileOpen: false})}>{t('ACTIONS')}</RouteLink>
                  <RouteLink activable={true} to={makeUserPath(competition.id, user, t)}  onClick={() => this.setState({isMobileOpen: false})}>{t('PROFILE')}</RouteLink>
                  <RouteLink activable={true} route={Routes.Account}  onClick={() => this.setState({isMobileOpen: false})}>{t('ACCOUNT')}</RouteLink>
                  {canManageEmailTemplates &&
                    <RouteLink activable={true} route={Routes.AdminGuide} onClick={() => this.setState({isMobileOpen: false})}>{t('ADMIN GUIDE')}</RouteLink>
                  }
                  {canManageEmailTemplates &&
                    <RouteLink activable={true} route={Routes.Totals} onClick={() => this.setState({isMobileOpen: false})}>{t('TOTALS')}</RouteLink>
                  }
                  {canManageEmailTemplates &&
                    <RouteLink activable={true} route={Routes.Communications} onClick={() => this.setState({isMobileOpen: false})}>{t('COMMUNICATIONS')}</RouteLink>
                  }
                  {canViewCommsTools &&
                    <RouteLink activable={true} route={Routes.Moderation} onClick={() => this.setState({isMobileOpen: false})}>{t('MODERATION')}</RouteLink>
                  }
                  {canManageEmailTemplates &&
                    <RouteLink activable={true} route={Routes.HeroNominations} onClick={() => this.setState({isMobileOpen: false})}>{t('HERO NOMINATIONS')}</RouteLink>
                  }
                  {canViewCommsTools && !canManageEmailTemplates &&
                    <RouteLink activable={true} route={Routes.SendMessage} onClick={() => this.setState({isMobileOpen: false})}>{t('SEND MESSAGE')}</RouteLink>
                  }
                  <RouteLink activable={true} route={Routes.Departments} onClick={() => this.setState({isMobileOpen: false})}>{campusesLabel.toUpperCase()}</RouteLink>
                  <RouteLink activable={true} route={Routes.Teams}  onClick={() => this.setState({isMobileOpen: false})}>{teamsLabel.toUpperCase()}</RouteLink>
                  <RouteLink activable={true} route={Routes.Calculator} onClick={() => this.setState({isMobileOpen: false})}>{t('CALCULATOR')}</RouteLink>
              </div>
            )}
            {!loggedIn && (
                <div  id="cc__nav_bar__mobile__links">
                  <RouteLink activable={true} route={Routes.About}>{t('ABOUT')}</RouteLink>
                  {!isProduction && Boolean(competition && competition.config && competition.config.blogUrl) && <RouteLink activable={true} route={Routes.Blog}>{t('BLOG')}</RouteLink>}
                  <Link to={makeFaqPath(FaqSection.General, t)}>{t('FAQ')}</Link>
                  <RouteLink activable={true} route={Routes.Departments}>{campusesLabel}</RouteLink>
                </div>
            )}
          </div>
          {loggedIn && (
            <div  id="cc__nav_bar__mobile__buttons">
              <Button
                color="primary"
                onClick={logout}
                style={{ width: '250px', margin: '0 auto'}}
              >{t('Sign out')}</Button>
            </div>
          )}
            {!loggedIn && (
              <div  id="cc__nav_bar__mobile__buttons">
                <Button
                  color="primary"
                  onClick={() => {history.push(makeSimpleRoutePath(Routes.Signup, t)); scrollToLoginForm(); }}
                  style={{ width: '250px', margin: '0 auto', marginBottom: 20}}
                >{t('Get started')}</Button>
                <Button
                  outline={true}
                  color="light"
                  onClick={() => {history.push(makeSimpleRoutePath(Routes.Login, t)); scrollToLoginForm(); }}
                  style={{ width: '250px', margin: '0 auto'}}
                >{t('Login')}</Button>
              </div>
            )}

        </div>
        <div
          id="cc__nav_bar__dropdown_toggle"
          onMouseEnter={this.openDesktopMenu}
          onMouseLeave={this.scheduleCloseDesktopMenu}
        >
          {loggedIn && (
            <LinkButton className="cc_nav_bar__desktop_menu" onClick={this.openDesktopMenu}>
              <ProfilePhoto
                rounded={true}
                outerStyle={{width: '60px', height: '60px'}}
                imgProps={{ style: {  cursor: 'pointer' } }}
                profilePhoto={user.profilePhoto}
                alt={user.firstName}
              />
            </LinkButton>
          )}
          <LinkButton
            className="cc_nav_bar__mobile_menu"
            onClick={() => this.openMobileDropdown()}
            aria-label={t('Open menu')}
            aria-expanded={this.state.isMobileOpen}
          >
            <span className="cc_nav_bar__mobile_menu__btn" aria-hidden={true}>
              <span className="cc_nav_bar__mobile_menu__bars">
                <span />
                <span />
                <span />
              </span>
              <span className="cc_nav_bar__mobile_menu__label">{t('Menu')}</span>
            </span>
          </LinkButton>
        </div>
        {/* trigger="" keeps open/close fully controlled so desktop hover delay works across the portal gap */}
        <Popover
          trigger=""
          placement="bottom"
          target="cc__nav_bar__dropdown_toggle"
          isOpen={this.state.isOpen}
          toggle={this.scheduleCloseDesktopMenu}
        >
          <PopoverBody
            style={{ padding: '0px' }}
            onMouseEnter={this.openDesktopMenu}
            onMouseLeave={this.scheduleCloseDesktopMenu}
          >
            <ListGroup>
              <ListGroupItem
                id="cc__nav_bar__dropdown__profile"
                style={{ border: 'none', cursor: 'pointer' }}
                onClick={() => history.push(makeUserPath(competition.id, user, t))}
              >{t('Profile')}</ListGroupItem>
              <ListGroupItem
                id="cc__nav_bar__dropdown__account"
                style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                onClick={() => history.push(makeSimpleRoutePath(Routes.Account, t))}
              >{t('Account')}</ListGroupItem>
              {canManageEmailTemplates &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__admin_guide"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.AdminGuide, t))}
                >{t('Admin Guide')}</ListGroupItem>
              }
              {canManageEmailTemplates &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__totals"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.Totals, t))}
                >{t('Totals')}</ListGroupItem>
              }
              {canManageEmailTemplates &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__communications"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.Communications, t))}
                >{t('Communications')}</ListGroupItem>
              }
              {canViewCommsTools &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__moderation"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.Moderation, t))}
                >{t('Moderation')}</ListGroupItem>
              }
              {canManageEmailTemplates &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__hero_nominations"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.HeroNominations, t))}
                >{t('Hero Nominations')}</ListGroupItem>
              }
              {canViewCommsTools && !canManageEmailTemplates &&
                <ListGroupItem
                  id="cc__nav_bar__dropdown__send_message"
                  style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                  onClick={() => history.push(makeSimpleRoutePath(Routes.SendMessage, t))}
                >{t('Send Message')}</ListGroupItem>
              }
              <ListGroupItem
                id="cc__nav_bar__dropdown__logout"
                style={{ borderLeft: 'none', borderRight: 'none', borderBottom: 'none', cursor: 'pointer' }}
                onClick={logout}
              >{t('Sign out')}</ListGroupItem>
            </ListGroup>
          </PopoverBody>
        </Popover>
      </div>
    );
  }

  private clearCloseTimer = () => {
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private openDesktopMenu = () => {
    this.clearCloseTimer();
    if (!this.state.isOpen) {
      this.setState({ isOpen: true });
    }
  }

  private scheduleCloseDesktopMenu = () => {
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => {
      this.setState({ isOpen: false });
      this.closeTimer = null;
    }, HOVER_CLOSE_DELAY_MS);
  }

  private openMobileDropdown() {
    this.openMobileMenu();
  }
}
